/* eslint-disable jsx-a11y/control-has-associated-label,jsx-a11y/alt-text */

'use client';

import { WebViewScreen } from '@diritto/common/app/webview-header.constant';
import Head from 'next/head';
import React, { useEffect } from 'react';
import { MutableHeaderLayout } from 'ui-components/layout/MutableHeaderLayout';
import { useInAppHeader } from 'ui-hooks/useInAppHeader';
import { useInAppStatusBar } from 'ui-hooks/useInAppStatusBar';
import { configure } from 'utils/configure';

const openImageUrl = `${configure.staticPrefix}/images/og/pick-and-choo.png?v=1`;

const ogTitle = '프롤로그 픽앤츄 with RIDI ';
const description = '독자들의 투표로 Pick and Choose한 작품의 디리토 연재부터 리디 출간까지 지원하는 PLAY 픽앤츄';
const url = 'https://www.diritto.co.kr/pick-and-choo';

export const ContestPage = ({ isApp }: { isApp?: boolean }) => {
  useInAppHeader({ screen: WebViewScreen.PickAndChoo, title: '프롤로그 픽앤츄 with RIDI' });
  useInAppStatusBar(WebViewScreen.PickAndChoo);

  return (
    <>
      <Head>
        <meta name="description" content={description} />
        <meta property="og:type" content="website" key="type" />
        <meta property="og:image" content={openImageUrl} key="image" />
        <meta property="og:title" content={ogTitle} key="title" />
        <meta property="og:description" content={description} key="description" />
        <meta property="og:url" content={url} key="url" />
        <meta property="og:site_name" content="디리토" key="site_name" />
        <meta property="twitter:image" content={openImageUrl} key="twitter:image" />
        <meta property="twitter:card" content="summary_large_image" key="twitter:card" />
        <meta property="twitter:site" content="@diritto_kr" key="twitter:site" />
        <meta property="twitter:title" content={ogTitle} key="twitter:title" />
        <meta property="twitter:description" content={description} key="twitter:description" />
        <link type="image/png" href={openImageUrl} />
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'http://schema.org',
              '@type': 'NewsArticle',
              mainEntityOfPage: {
                '@type': 'WebPage',
                '@id': 'https://google.com/article',
              },
              headline: ogTitle,
              image: [openImageUrl],
              url,
              description,
            }),
          }}
        />
      </Head>
      <PickAndChooAccepted isApp={isApp} />
    </>
  );
};

const useResponsiveImageMap = (imageId: string, mapName: string) => {
  useEffect(() => {
    const resizeMap = () => {
      const image = document.getElementById(imageId) as HTMLImageElement;
      if (!image) {
        return;
      }

      const map = document.querySelector(`map[name="${mapName}"]`) as HTMLMapElement;
      if (!map) {
        return;
      }

      const imageWidth = image.offsetWidth;
      const originalWidth = image.naturalWidth;
      const scale = imageWidth / originalWidth;

      map.querySelectorAll('area').forEach(area => {
        const originalCoords = area.dataset.originalCoords || area.getAttribute('coords');
        // eslint-disable-next-line no-param-reassign
        area.dataset.originalCoords = originalCoords || '';
        if (originalCoords) {
          const coords = originalCoords.split(',').map(coord => Math.round(parseInt(coord, 10) * scale));
          area.setAttribute('coords', coords.join(','));
        }
      });
    };

    window.addEventListener('resize', resizeMap);
    resizeMap();

    return () => {
      window.removeEventListener('resize', resizeMap);
    };
  }, [imageId, mapName]);
};

export const PickAndChooAccepted = ({ isApp }: { isApp?: boolean }) => {
  useResponsiveImageMap('image1', 'image-map1');
  useResponsiveImageMap('image2', 'image-map2');
  useResponsiveImageMap('image3', 'image-map3');
  useResponsiveImageMap('image4', 'image-map4');

  return (
    <MutableHeaderLayout noBottomPadding isApp={isApp}>
      <div className="relative flex flex-col items-center gap-0">
        <div className="flex justify-center w-full bg-[#EFEBFA]">
          <img
            alt="event"
            src={`${configure.staticPrefix}/images/contests/1/contests-v-1.png`}
            className="w-full max-w-[390px] h-auto"
          />
        </div>
        <div className="flex justify-center w-full bg-[#E2DAF7]">
          <img
            alt="event"
            src={`${configure.staticPrefix}/images/contests/1/accepted-9.png`}
            className="w-full max-w-[390px] h-auto"
          />
        </div>
        <div className="flex justify-center w-full bg-[#322E36]">
          <img
            alt="event"
            src={`${configure.staticPrefix}/images/contests/1/accepted-1.png`}
            className="w-full max-w-[390px] h-auto"
          />
        </div>
        <div className="flex justify-center w-full bg-white">
          <img
            alt="event"
            src={`${configure.staticPrefix}/images/contests/1/accepted-13.png`}
            className="w-full max-w-[390px] h-auto"
          />
        </div>
        <div className="flex justify-center w-full bg-white">
          <img
            alt="event"
            id="image1"
            src={`${configure.staticPrefix}/images/contests/1/accepted-10.png`}
            useMap="#image-map1"
            className="w-full max-w-[390px] h-auto"
          />
          <map name="image-map1">
            <area
              target="_self"
              href="https://www.diritto.co.kr/contents/2387408"
              coords="57,354,530,1486"
              shape="rect"
            />
            <area
              target="_self"
              href="https://www.diritto.co.kr/contents/2387044"
              coords="1020,1561,542,353"
              shape="rect"
            />
            <area
              target="_self"
              href="https://www.diritto.co.kr/contents/2387226"
              coords="1030,349,1518,1525"
              shape="rect"
            />
            <area
              target="_self"
              href="https://www.diritto.co.kr/contents/2387010"
              coords="297,1634,774,2686"
              shape="rect"
            />
            <area
              target="_self"
              href="https://www.diritto.co.kr/contents/2386908"
              coords="788,1636,1267,2675"
              shape="rect"
            />
          </map>
        </div>
        <div className="flex justify-center w-full bg-[#E2DAF7]">
          <img
            id="image2"
            alt="event"
            src={`${configure.staticPrefix}/images/contests/1/accepted-11.png`}
            useMap="#image-map2"
            className="w-full max-w-[390px] h-auto"
          />
          <map name="image-map2">
            <area
              target="_self"
              href="https://www.diritto.co.kr/contents/2386913"
              coords="37,347,526,1461"
              shape="rect"
            />
            <area
              target="_self"
              href="https://www.diritto.co.kr/contents/2387126"
              coords="544,350,1009,1455"
              shape="rect"
            />
            <area
              target="_self"
              href="https://www.diritto.co.kr/contents/2386700"
              coords="1025,351,1518,1415"
              shape="rect"
            />
            <area
              target="_self"
              href="https://www.diritto.co.kr/contents/2386884"
              coords="290,1552,775,2657"
              shape="rect"
            />
            <area
              target="_self"
              href="https://www.diritto.co.kr/contents/2387243"
              coords="792,1557,1258,2650"
              shape="rect"
            />
          </map>
        </div>
        <div className="flex justify-center w-full bg-[#EFEBFA]">
          <img
            id="image3"
            alt="event"
            src={`${configure.staticPrefix}/images/contests/1/accepted-12.png`}
            useMap="#image-map3"
            className="w-full max-w-[390px] h-auto"
          />
          <map name="image-map3">
            <area
              target="_self"
              href="https://www.diritto.co.kr/contents/2386780"
              coords="39,353,522,1461"
              shape="rect"
            />
            <area
              target="_self"
              href="https://www.diritto.co.kr/contents/2387256"
              coords="534,349,1012,1450"
              shape="rect"
            />
            <area
              target="_self"
              href="https://www.diritto.co.kr/contents/2386321"
              coords="1023,351,1504,1417"
              shape="rect"
            />
            <area
              target="_self"
              href="https://www.diritto.co.kr/contents/2386972"
              coords="282,1543,775,2584"
              shape="rect"
            />
            <area
              target="_self"
              href="https://www.diritto.co.kr/contents/2386638"
              coords="788,1548,1259,2661"
              shape="rect"
            />
          </map>
        </div>
        <div className="h-[300px] w-full bg-[#EFEBFA]" />
      </div>
    </MutableHeaderLayout>
  );
};
